import React, { useState, useEffect, useMemo } from "react";
import { HelpCircle } from "lucide-react";

// Tooltip definitions
const tooltipDefinitions = {
  PTS: "A player's predicted points per 100 possessions based on predicted shot usage and efficiency.",
  "2PT%":
    "A player's predicted two-point shooting percentage against an average opponent, adjusted for usage.",
  "3PT%":
    "A player's predicted three-point shooting percentage against an average opponent, adjusted for usage.",
  "FT%":
    "A player's predicted free throw shooting percentage against an average opponent.",
  REB: "A player's predicted offensive + defensive rebounding rate. See O-Reb % and D-Reb % for more detail.",
  AST: "A player's predicted assist rate against an average opponent, adjusted for usage. Assist Rate is the percentage of teammate made field goals that the player assisted while on the court.",
  "Steal Rate":
    "A player's predicted steal percentage against an average opponent. Steal percentage is percentage of defensive possessions that end in the player stealing the ball while on the court.",
  BLK: "A player's predicted block percentage against an average opponent, adjusted for usage.. Block percentage is the percentage of opponent two-point field goal attempts that the player blocked while on the court.",
  "Turn Over":
    "A player's predicted turnover percentage against an average opponent, adjusted for usage. Turnover percentage is the percentage of offensive possessions that end in the player turning the ball over while on the court.",
  PF: "A player's predicted personal fouls committed per 100 possessions.",
  Season:
    "The specific basketball season (year) in which the player's predicted stats and values are calculated.",
  Poss: "The number of possessions that a player is predicted to be involved in. A possession refers to a team's opportunity to score before the other team gains control of the ball.",
  "+/-":
    "A player's predicted plus-minus rating, which measures the point differential (points scored by the player's team minus points scored by the opponent) per 100 possessions while the player is on the court, against an average opponent.",
  Team: "The team that the player is predicted to play for in the given season.",
  GP: "Games Played - The number of games the player participated in during the season.",
  MIN: "Minutes Per Game - Average minutes played per game.",
  "FG%":
    "Field Goal Percentage - The percentage of field goals made out of total attempts.",
};

// eslint-disable-next-line no-unused-vars
const PracticeStatisticsTable = ({
  player,
  isDarkMode = true, // OLED: default to dark
  headerConfig = "advanced", // Can be 'basic', 'advanced', 'custom', or an array of header objects
  customHeaders = null, // Optional custom headers array
  error = null
}) => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [hideTimeoutId, setHideTimeoutId] = useState(null);

  // Determine which headers to use

  const headers = useMemo(() => {
    const keysSet = new Set();
    if (player?.data?.length > 0) {
      keysSet.add("season");
      player.data.forEach((entry) => {
        if (entry.stats) {
          Object.keys(entry.stats).forEach((key) => keysSet.add(key));
        }
      });
    }
    return Array.from(keysSet);
  }, [player]);

  // Add scroll event listener to hide tooltip when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (showTooltip) {
        setShowTooltip(false);
        // Clear any pending timeout when scrolling
        if (hideTimeoutId) {
          clearTimeout(hideTimeoutId);
          setHideTimeoutId(null);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showTooltip, hideTimeoutId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutId) {
        clearTimeout(hideTimeoutId);
      }
    };
  }, [hideTimeoutId]);

  const handleTooltipShow = (e, content) => {
    // Clear any pending hide timeout
    if (hideTimeoutId) {
      clearTimeout(hideTimeoutId);
      setHideTimeoutId(null);
    }

    const thElement = e.target.closest("th");
    if (thElement) {
      const rect = thElement.getBoundingClientRect();
      setTooltipContent(content);
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 2,
      });
      setShowTooltip(true);
    }
  };

  const handleTooltipHide = () => {
    // Clear any existing timeout first
    if (hideTimeoutId) {
      clearTimeout(hideTimeoutId);
    }

    // Set a new timeout to hide the tooltip
    const timeoutId = setTimeout(() => {
      setShowTooltip(false);
      setHideTimeoutId(null); // Clear the timeout ID after execution
    }, 200);
    setHideTimeoutId(timeoutId);
  };

  const Tooltip = () => {
    if (!showTooltip) return null;

    return (
      <div
        style={{
          position: "fixed",
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          transform: "translateX(-50%) translateY(-100%)",
          backgroundColor: "#111111",
          color: "#F5F5F5",
          padding: "8px 12px",
          borderRadius: "6px",
          fontSize: "12px",
          maxWidth: "300px",
          zIndex: 10000,
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)",
          lineHeight: "1.4",
          pointerEvents: "none",
        }}
      >
        {tooltipContent}
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid #111111",
          }}
        />
      </div>
    );
  };

  const TableHeader = ({ children, hasTooltip = false }) => (
    <th
      className="py-3 px-4 text-left font-semibold text-[#999999]"
      onMouseEnter={
        hasTooltip && tooltipDefinitions[children]
          ? (e) => handleTooltipShow(e, tooltipDefinitions[children])
          : undefined
      }
      onMouseLeave={
        hasTooltip && tooltipDefinitions[children]
          ? handleTooltipHide
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {hasTooltip && tooltipDefinitions[children] && (
          <HelpCircle
            size={12}
            style={{
              color: "#555555",
              opacity: 0.7,
              cursor: "help",
            }}
          />
        )}
      </div>
    </th>
  );

  return (
    <>
      <Tooltip />

      <div className="mt-4 bg-[#111111] rounded-xl shadow-sm border border-[#1A1A1A] overflow-hidden">
        <div className="p-6">
          <div className="overflow-x-auto">
            <div className="overflow-y-auto max-h-[450px] relative">
              <table className="w-full">
                <thead className="bg-[#1A1A1A] text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    {headers &&
                      headers.map((header, index) => (
                        <TableHeader
                          key={index}
                          hasTooltip
                          className="min-w-[120px] first:min-w-[150px] first:sticky first:left-0 first:z-20"
                        >
                          {header}
                        </TableHeader>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y bg-[#0A0A0A] divide-[#1A1A1A]">
                  {player?.data ? (
                    player.data
                      .filter((entry) => entry.stats)
                      .map((entry, index) => (
                        <tr
                          key={entry.stat_id || index}
                          className={index % 2 === 0 ? 'bg-[#0A0A0A]' : 'bg-[#111111]'}
                        >
                          <td className="z-10 px-4 py-3 text-sm font-medium text-[#999999] border-r border-[#1A1A1A] whitespace-nowrap bg-inherit">
                            {entry.session_date
                              ? new Date(entry.session_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  }
                                )
                              : "N/A"}
                          </td>
                          {headers.map(
                            (key) =>
                              key !== "season" && (
                                <td
                                  key={`${entry.stat_id || index}-${key}`}
                                  className="px-6 py-3 text-sm text-[#999999] whitespace-nowrap"
                                >
                                  {entry.stats?.[key]
                                    ? entry.stats?.[key]
                                    : "N/A"}
                                </td>
                              )
                          )}
                        </tr>
                      ))
                  ) : (
                    <tr className="text-center">{error}</tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PracticeStatisticsTable;
